import React, { PureComponent } from 'react'
import { Button, Col, Container, Row, Spinner, Table } from 'react-bootstrap'
import { Subscription, BehaviorSubject, of, concat } from "rxjs"
import { distinctUntilChanged, switchMap, map, catchError } from "rxjs/operators"
import { toast } from "react-toastify"
import { connect } from "react-redux"
import { AppState } from '../../../AppState'
import { Filter, Sort } from "../../../api/GridRequest"
import { CustomizableFilterColumn } from '../../ColumnsCustomizeModal/CustomizableColumn'
import { AppPageWrapper } from '../../NavMenu/AppPageWrapper'
import TablePagination from '../../TablePagination'
import SearchField from '../../../api/SearchField'
import SearchBar from '../../SearchBar'
import FormatError from '../../../formatters/FormatError'
import ColumnHeaderFilter from '../../ColumnHeaderFilter'
import PortalGridRow from '../../../api/Invitation/Portal/PortalGridRow'
import { PortalColumnFilter } from '../../../api/Invitation/Portal/PortalColumnFilter'
import PortalGridData from '../../../api/Invitation/Portal/PortalGridData'
import { PortalGridGetObservable } from '../../../api/Invitation/Portal/PortalGridGetObservable'
import { isSiteAdmin } from '../../../authentication/isAdminRole'
import UserInfo, { UserInfoMode } from '../../Administration/UserInfo'
import { ImplementationGroupID_Workforce } from '../../../config/Constants'
import { AddNewPortalRoute, PortalDetailsRoute } from '../../../config/Routes'
import { Link } from 'react-router-dom'
import FormatPhone from '../../../formatters/FormatPhone'
import FormatZipCode from '../../../formatters/FormatZipCode'
import {RouteComponentProps} from "react-router";
import AddNewAdminModal from './AddNewAdminModal'

type Column = CustomizableFilterColumn<keyof PortalGridRow, keyof PortalColumnFilter>

interface PortalGridState {
  loading: boolean
  data?: PortalGridData
  details?: PortalGridRow
  columns: Column[]
  showUserInfo: boolean
  showUserInfoFor?: string
  participationId?: number
  showAddAdmin: boolean
}

interface PortalGridProps {
  isSiteAdmin?: boolean
}

const PageSize = 30

const DefaultSearchFields: SearchField[] = [
  { ID: "ImplementationName", Name: "Portal Name" },
  { ID: "Email", Name: "Email" }
]

const DefaultColumns: Column[] = [
  { name: "ImplementationName", label: "Portal Name", visible: true},
  { name: "ImplementationType", label: "Type", visible: true },
  { name: "CompanyName", label: "Company Name", visible: true },
  { name: "Address", label: "Address", visible: true },
  { name: "City", label: "City", visible: true },
  { name: "StateCode", label: "State", visible: true },
  { name: "Zip", label: "Zip", visible: true },
  { name: "Email", label: "Admin Email", visible: true },
  { name: "Phone", label: "Admin Phone", visible: true },
  { name: "Inactive", label: "Active/Inactive", visible: true }
]

interface GridSearchParams {
  pageNumber: number
  sort: Sort
  filters: Filter[]
  search: { field: string, text: string }
}

class PortalGrid extends PureComponent<PortalGridProps, PortalGridState> {
  state: PortalGridState = {
    loading: false,
    columns: DefaultColumns,
    showUserInfo: false,
    showAddAdmin: false
  }

  page$ = new BehaviorSubject<GridSearchParams>({
    pageNumber: 1,
    sort: { column: "", order: "" },
    filters: [],
    search: { field: "", text: "" }
  })

  sortGrid(column: string, order?: string) {
    const newSort = { column: column, order: order || "" }
    this.page$.next({ ...this.page$.value, sort: newSort })
  }

  filterGrid(column: string, items: string[]) {
    const filters = [...this.page$.value.filters]
    const filter = { column: column, values: items }
    let index = filters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      filters[index] = filter
    } else {
      filters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, filters: filters })
  }

  onSearch = (field: string, text: string) => this.page$.next({ ...this.page$.value, pageNumber: 1, search: { field: field, text: text } })
  handlePageChange = (pageNumber: number) => this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  handleAddPortal = () => (this.props as RouteComponentProps).history?.push(AddNewPortalRoute)
  reload = () => this.page$.next({ ...this.page$.value, pageNumber: 1 })
  hideUserInfo = () => this.setState({ showUserInfoFor: undefined, showUserInfo: false })
  showUserInfo = (data: PortalGridRow) => {
    this.setState({ showUserInfoFor: data.UserName, participationId: data.ParticipationID, showUserInfo: true })
  }
  handleAddAdminModalClose = (hasUpdates: boolean) => {
    this.setState({ showAddAdmin: false })
    if (hasUpdates) {
      this.handlePageChange(1)
    }
  }

  pageSubscription?: Subscription

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap((params: GridSearchParams) => concat(
        of({ loading: true }),
        PortalGridGetObservable(params.pageNumber, PageSize, params.sort, params.filters, params.search, ImplementationGroupID_Workforce).pipe(
          map((result: PortalGridData) => ({ data: result, loading: false })),
          catchError((error) => of({ loading: false, errorMessage: FormatError(error) }))
        )))
    ).subscribe(
      (newState: { loading: boolean, data?: PortalGridData, errorMessage?: string }) => {
        if (newState.data) {
          this.setState({ loading: newState.loading, data: newState.data })
        } else {
          this.setState({ loading: newState.loading })
        }
        newState.errorMessage && toast.error(newState.errorMessage)
      },
      (error) => {
        this.setState({ loading: false })
        toast.error(FormatError(error))
      }
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.page$.unsubscribe()
  }

  visibleColumns = () => this.state.columns.filter(col => col.visible)

  renderHeader = () => this.visibleColumns().map((col: Column, i) => {
    const sort = this.page$.value.sort
    return (
      <th key={col.name}>
        {this.state.data && col.filter && col.filter in this.state.data && <ColumnHeaderFilter
          title={col.label || col.name}
          filteredItems={this.page$.value.filters.find(item => item.column === col.filter)?.values || []}
          sort={sort.column === col.name ? sort.order === "asc" ? "asc" : "desc" : undefined}
          items={this.state.data[col.filter as keyof PortalColumnFilter]?.filter(item => !!item) || []}
          onSort={(order?: string) => this.sortGrid(col.name, order)}
          onFilter={(items: string[]) => col.filter && this.filterGrid(col.filter, items)}
        />}
        {(this.state.data && !col.filter) && <span className="column-header-title text-uppercase mr-1">{col.label || col.name}</span>}
      </th>
    )
  })

  renderRows = () => {
    if (this.state.data?.Records && this.state.data?.Records.length) {
      return this.state.data.Records.map((row: PortalGridRow, i: number) =>
        <tr key={i}>
          <td><Link className="btn-link btn-icon-link" to={{ pathname: PortalDetailsRoute(row.ImplementationName) }}>{row.ImplementationName}</Link></td>
          <td>{row.ImplementationType}</td>
          <td>{row.CompanyName}</td>
          <td>{row.Address}</td>
          <td>{row.City}</td>
          <td>{row.StateCode}</td>
          <td>{FormatZipCode(row.Zip)}</td>
          <td><Button variant="link" onClick={() => this.showUserInfo(row)} className="p-0">{row.Email}</Button></td>
          <td>{FormatPhone(row.Phone)}</td>
          <td>{row.Inactive ? "Inactive" : "Active"}</td>
        </tr>)
    } else {
      return <tr><td colSpan={9}>No Records Found</td></tr>
    }
  }

  render() {
    return (
      <AppPageWrapper parentClassName={"align-items-center"} showNav>
        <Container fluid className="grid-container">
          <Row className="pb-2">
            <Col>
              <UserInfo
                show={!!this.state.showUserInfoFor}
                mode={UserInfoMode.Team}
                onClose={this.hideUserInfo}
                LoginName={this.state.showUserInfoFor}
                participationId={this.state.participationId} />
              <SearchBar
                allowBlank
                resetButton
                onSearch={this.onSearch}
                searchFields={DefaultSearchFields}
                loading={this.state.loading}
              />
            </Col>
            <Col md={8} className="text-md-right mt-2 mt-md-0">
              <Button variant="primary" size="sm" onClick={this.handleAddPortal} className="mr-2">Add Portal</Button>
              <Button variant="primary" size="sm" onClick={() => this.setState({ showAddAdmin: true })}>Add Portal Admin</Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <Table bordered responsive>
                <thead>
                  <tr>
                    {this.renderHeader()}
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.loading ?
                      <tr>
                        <td className="text-center" colSpan={9}>
                          <Spinner animation="border" variant="secondary" />
                        </td>
                      </tr> : this.renderRows()
                  }
                </tbody>
              </Table>
            </Col>
          </Row>
          {this.state.data ?
            <Row>
              <Col>
                <span className="total-records">Total records: {this.state.loading ? " ... " : this.state.data.Total}</span>
                <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.handlePageChange} />
              </Col>
            </Row> : undefined}
        </Container>
        {this.state.showAddAdmin && <AddNewAdminModal show={this.state.showAddAdmin} onClose={this.handleAddAdminModalClose}/>}
      </AppPageWrapper>
    )
  }
}

function mapStateToProps(state: AppState): PortalGridProps {
  return {
    isSiteAdmin: isSiteAdmin(state)
  }
}
const ConnectedPortalGrid = connect(mapStateToProps, null)(PortalGrid)
export default ConnectedPortalGrid
